import { useEffect, useState } from 'react'
import {
	AppButton,
	Divider,
	Modal,
	ModalHeader,
	Input,
	DotMatrix,
	Toggle
} from '@clickguard/design-system'
import AnnotationPlus from '@untitled-ui/icons-react/build/esm/AnnotationPlus'
import { CrmMark } from './crm-marks'
import { CrmApi, CrmConnection, CrmFormReference } from 'services/crm/api'
import { useHubSpotConnect } from 'services/crm/use-hubspot-connect'
import { errorMessage } from 'pages/workspace/asset/lead-forms/api'

export const NewCrmModal = ({
	open,
	onClose,
	api,
	assetId,
	onConnected
}: {
	open: boolean
	onClose: () => void
	api: CrmApi
	assetId?: string
	onConnected: () => void
}) => {
	const [connection, setConnection] = useState<CrmConnection | null>(null)
	const [request, setRequest] = useState(false)
	const [message, setMessage] = useState('')
	const [sent, setSent] = useState(false)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState('')
	const [makeDefault, setMakeDefault] = useState(false)
	const [affected, setAffected] = useState<CrmFormReference[] | null>(null)
	const oauth = useHubSpotConnect(open ? api : undefined, (c) => {
		setConnection(c)
		if (assetId)
			void api
				.setPreference(assetId, c.id)
				.catch(() =>
					setError(
						'HubSpot connected, but the website default could not be saved. Choose Finish to try again.'
					)
				)
		onConnected()
	})
	useEffect(() => {
		if (open) {
			setConnection(null)
			setRequest(false)
			setMessage('')
			setSent(false)
			setError('')
			setMakeDefault(false)
			setAffected(null)
		}
	}, [open])
	const finish = async (confirm = false) => {
		if (!connection || !assetId) {
			onClose()
			return
		}
		setSaving(true)
		setError('')
		try {
			if (makeDefault && !confirm) {
				const result = await api.defaultImpact(assetId, connection.id)
				if (result.forms.length) {
					const verification = await api.verify(connection.id, assetId)
					if (verification.status !== 'valid') {
						setError(
							`The Deliverability (ClickGuard) property is ${verification.status} in HubSpot. Create a single-line text property with that label, or fix its type, then choose Finish to retry. Existing form connections have not changed.`
						)
						return
					}
					setAffected(result.forms)
					return
				}
			}
			if (makeDefault && affected) {
				const result = await api.applyDefault(
					assetId,
					connection.id,
					affected.map(({ id, settings_version }) => ({ id, settings_version }))
				)
				if (result.skipped) {
					setAffected((await api.defaultImpact(assetId, connection.id)).forms)
					setError(
						`${result.updated} forms updated. ${result.skipped} forms changed while you were confirming. Review the updated list and try again.`
					)
					onConnected()
					return
				}
			}
			await api.setPreference(assetId, connection.id)
			onConnected()
			onClose()
		} catch (e) {
			setError(errorMessage(e))
		} finally {
			setSaving(false)
		}
	}
	return (
		<Modal open={open} onClose={onClose} size="md">
			<div className="flex flex-col gap-6 p-8">
				<ModalHeader
					size="sm"
					titleColor="navy"
					title="New CRM"
					subtitle="Connect your CRM to flag lead deliverability."
					onClose={onClose}
				/>
				{(error || oauth.error) && (
					<p role="alert" className="text-sm text-red-600">
						{error || oauth.error}
					</p>
				)}
				{connection ? (
					<div className="flex flex-col gap-4">
						<p>
							Connected to {connection.name} ({connection.portal_id})
						</p>
						{assetId && (
							<>
								<p className="text-sm cg-text-text-secondary">
									Use this connection for new forms on this website.
								</p>
								<Toggle
									checked={makeDefault}
									onChange={(e) => setMakeDefault(e.target.checked)}
								/>
								<span>Make default for all existing forms</span>
							</>
						)}
						{affected && (
							<div>
								<p>
									The required HubSpot property is verified. Switch these forms
									to this connection? Their On/Off settings will stay the same.
								</p>
								<ul>
									{affected.map((f) => (
										<li key={f.id}>{f.form_name}</li>
									))}
								</ul>
							</div>
						)}
						{saving ? (
							<DotMatrix label="Saving CRM preference" />
						) : (
							<AppButton
								label={affected ? 'Confirm and finish' : 'Finish'}
								onClick={() => void finish(!!affected)}
							/>
						)}
					</div>
				) : (
					<div className="flex flex-col gap-4">
						{(['hubspot', 'brevo'] as const).map((provider) => (
							<div key={provider} className="flex w-full items-center gap-3">
								<div className="flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-lg border cg-border-border-default cg-bg-surface shadow-sm">
									<CrmMark provider={provider} />
								</div>
								<div className="min-w-0 flex-1">
									<p className="text-sm font-medium cg-text-text-primary">
										{provider === 'hubspot' ? 'HubSpot' : 'Brevo'}
									</p>
									<p className="text-sm cg-text-text-secondary">
										{provider === 'hubspot'
											? 'Connect your CRM with a secure login'
											: 'Connect your CRM with an API key'}
									</p>
								</div>
								{provider === 'hubspot' && oauth.busy ? (
									<DotMatrix label="Connecting HubSpot" />
								) : (
									<AppButton
										variant="outline"
										size="sm"
										label={provider === 'hubspot' ? 'Connect' : 'Coming soon'}
										disabled={provider === 'brevo'}
										onClick={() => void oauth.connect()}
									/>
								)}
							</div>
						))}
						<Divider />
						<div className="flex items-center gap-3">
							<AnnotationPlus className="h-6 w-6 cg-text-brand" />
							<p className="flex-1 text-sm">Request a new CRM integration</p>
							<AppButton
								variant="outline"
								size="sm"
								label="Request"
								onClick={() => setRequest(true)}
							/>
						</div>
					</div>
				)}
				{request && (
					<div className="flex flex-col gap-3">
						{sent ? (
							<p role="status">Your integration request has been sent.</p>
						) : (
							<>
								<Input
									state="default"
									aria-label="CRM integration request"
									placeholder="Which CRM would you like to connect?"
									value={message}
									onChange={(e) => setMessage(e.target.value)}
								/>
								{saving ? (
									<DotMatrix label="Sending request" />
								) : (
									<AppButton
										label="Send request"
										disabled={!message.trim()}
										onClick={async () => {
											setSaving(true)
											setError('')
											try {
												await api.request(message)
												setSent(true)
											} catch (e) {
												setError(errorMessage(e))
											} finally {
												setSaving(false)
											}
										}}
									/>
								)}
							</>
						)}
					</div>
				)}
			</div>
		</Modal>
	)
}
